import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { COMPANY } from "../constants/company";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function Auth() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") {
        await login(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password);
      }
      toast.success(tab === "login" ? "Welcome back" : "Account created");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="auth-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src={COMPANY.images.tax} alt="Tax documents" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="relative z-10 p-12 flex flex-col justify-between text-primary-foreground">
          <Link to="/" data-testid="auth-logo-link" className="flex items-center">
            <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-16 w-auto rounded-md bg-white/95 p-1.5" />
          </Link>
          <div>
            <h1 className="text-4xl lg:text-5xl font-heading font-semibold tracking-tight leading-tight">
              Your taxes, handled with care.
            </h1>
            <p className="mt-4 text-primary-foreground/80 max-w-md">
              Complete your intake, upload documents securely, and book time with our Taylorsville team — all in one place.
            </p>
          </div>
          <p className="text-sm text-primary-foreground/60">{COMPANY.fullAddress} · {COMPANY.phone}</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-fade-up">
          <Link to="/" data-testid="auth-home-link" className="lg:hidden flex items-center mb-8">
            <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-12 w-auto rounded-md" />
          </Link>
          <h2 className="text-3xl font-heading font-semibold tracking-tight" data-testid="auth-heading">
            {tab === "login" ? "Sign in to your account" : "Create your account"}
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">Client portal of {COMPANY.name}</p>

          <Tabs value={tab} onValueChange={setTab} className="mt-6">
            <TabsList className="grid w-full grid-cols-2" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="auth-tab-login">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="auth-tab-register">Register</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="login-form">
                <div className="space-y-1.5">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input id="login-email" data-testid="login-email-input" type="email" required className="pl-9" placeholder="you@example.com" value={form.email} onChange={set("email")} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input id="login-password" data-testid="login-password-input" type="password" required className="pl-9" placeholder="••••••••" value={form.password} onChange={set("password")} />
                  </div>
                </div>
                <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full">
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="register">
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="register-form">
                <div className="space-y-1.5">
                  <Label htmlFor="reg-name">Full name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input id="reg-name" data-testid="register-name-input" required className="pl-9" placeholder="Jane Doe" value={form.name} onChange={set("name")} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input id="reg-email" data-testid="register-email-input" type="email" required className="pl-9" placeholder="you@example.com" value={form.email} onChange={set("email")} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input id="reg-password" data-testid="register-password-input" type="password" required minLength={6} className="pl-9" placeholder="Min. 6 characters" value={form.password} onChange={set("password")} />
                  </div>
                </div>
                <Button data-testid="register-submit-button" type="submit" disabled={loading} className="w-full">
                  {loading ? "Creating…" : "Create account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-widest">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <Button data-testid="google-login-button" variant="outline" className="w-full" onClick={googleLogin} type="button">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </Button>
          <p className="text-xs text-muted-foreground mt-6 text-center">
            Need help? <Link to="/support" data-testid="auth-support-link" className="text-primary underline underline-offset-2">Contact support</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
